export const DEFAULT_NAME = "default";

/**
 * A simple class to track the active tab in a group of tabs. It is
 * capable of encoding and decoding itself for use in a URL.
 */
export class ManagedTabsState {
  private readonly active: Record<string, string>;

  constructor(tabs: Record<string, string> = {}) {
    this.active = tabs;
  }

  /**
   * Designates the active tab for the named group of tabs.
   *
   * @param name The name of the group of tabs
   * @param tabId The id of the active tab for the specified group
   */
  setActive(name: string, tabId: string): ManagedTabsState {
    return new ManagedTabsState({...this.active, [name]: tabId});
  }

  /**
   * Returns the active tab for the named group of tabs.
   *
   * @param name The name of the group of tabs
   * @param defaultTabId The default tab when there is no currently active tab
   * @return The name of the active tab for the group; defaultTabId if not defined
   */
  getActive(name: string, defaultTabId?: string): string | undefined {
    return this.active[name] || defaultTabId;
  }

  /**
   * Encodes this {@link ManagedTabsState} instance suitable for use in a URL
   *
   * @return An encoded form of this {@link ManagedTabsState} for use in a URL
   */
  encode(): string {
    return Object.entries(this.active)
      .map(([name, tabId]: string[]): string | undefined => {
        const trimmedName = name?.trim();
        const trimmedTabId = tabId?.trim();
        if (trimmedName === DEFAULT_NAME && trimmedTabId?.length > 0) {
          return trimmedTabId;
        } else if (trimmedName?.length > 0 && trimmedTabId?.length > 0) {
          return `${trimmedName}:${trimmedTabId}`;
        }
        return undefined;
      })
      .filter(entry => !!entry)
      .join("::");
  }

  /**
   * Decodes a {@link ManagedTabsState} from its encoded form
   *
   * @param encoded The encoded form of this {@link ManagedTabsState}
   */
  static decode(encoded?: string | null): ManagedTabsState {
    return new ManagedTabsState(
      encoded?.split("::")?.reduce(
        (active, encodedEntry) => {
          const entry = encodedEntry.split(":");
          if (entry.length === 1) {
            active[DEFAULT_NAME] = entry[0];
          } else if (entry.length === 2) {
            active[entry[0]] = entry[1];
          }
          return active;
        },
        <Record<string, string>>{}
      )
    );
  }
}
